import { RoleOptions, AccountType, OfferStatus, InquiryPublicDto, NON_EDITABLE_INQUIRY_STATUSES, InquiryStatus } from '@paladin-monorepo/common';
import { UserProps } from './auth';

export function isSystemAdmin(user?: UserProps | null): boolean {
  if (!user) return false;
  return user.role === RoleOptions.SUPERADMIN || user.role === RoleOptions.SUPPORT;
}

export function canViewAllProjects(user?: UserProps): boolean {
  if (!user) return false;
  return user?.role === RoleOptions.SUPERADMIN || user?.role === RoleOptions.SUPPORT;
}

export function canCreateInquiry(user?: UserProps): boolean {
  if (!user) return false;
  return user?.account?.accountType === AccountType.Inquirer &&
    (user?.role === RoleOptions.ACCOUNT_OWNER || user?.role === RoleOptions.ACCOUNT_ADMIN || user?.role === RoleOptions.EMPLOYEE);
}

export function canEditInquiry(user?: UserProps, inquiry?: InquiryPublicDto): boolean {
  if (inquiry?.capabilities) {
    return inquiry.capabilities.edit;
  }
  if (!user || !inquiry) return false;
  // SUPERADMIN/SUPPORT can edit all briefs
  if (user?.role === RoleOptions.SUPERADMIN || user?.role === RoleOptions.SUPPORT) {
    return true;
  }

  if (NON_EDITABLE_INQUIRY_STATUSES.includes(inquiry.status)) {
    return false;
  }

  // ACCOUNT_ADMIN/EMPLOYEE can only edit their own account's briefs
  return user?.account?.accountType === AccountType.Inquirer &&
    user?.account?.id === inquiry.accountId;
}

export function canDeleteInquiry(user?: UserProps, inquiry?: InquiryPublicDto): boolean {
  if (inquiry?.capabilities) {
    return inquiry.capabilities.delete;
  }
  if (!user) return false;
  if (user.role === RoleOptions.SUPERADMIN) return true;
  if (!inquiry) return false;
  if (![InquiryStatus.Draft, InquiryStatus.InReview].includes(inquiry.status)) return false;
  return (
    user.account?.accountType === AccountType.Inquirer &&
    user.account?.id === inquiry.accountId &&
    (user.role === RoleOptions.ACCOUNT_OWNER ||
      user.role === RoleOptions.ACCOUNT_ADMIN ||
      user.role === RoleOptions.EMPLOYEE)
  );
}

export function canPublishInquiry(user?: UserProps, inquiry?: InquiryPublicDto): boolean {
  if (inquiry?.capabilities) {
    return inquiry.capabilities.publish;
  }
  if (!user || !inquiry) return false;
  // SUPERADMIN/SUPPORT can publish all briefs
  if (user?.role === RoleOptions.SUPERADMIN || user?.role === RoleOptions.SUPPORT) {
    return true;
  }

  // ACCOUNT_ADMIN/EMPLOYEE can only publish their own account's briefs
  return user?.account?.accountType === AccountType.Inquirer &&
    user?.account?.id === inquiry.accountId;
}

export function shouldShowInquiries(user?: UserProps): boolean {
  if (!user) return false;
  return user?.account?.accountType === AccountType.Consultant ||
    user?.account?.accountType === AccountType.SolutionProvider;
}

export function shouldShowProjects(user?: UserProps): boolean {
  if (!user) return false;
  return user?.account?.accountType === AccountType.Consultant ||
    user?.account?.accountType === AccountType.SolutionProvider;
}

export function canAccessProjects(user?: UserProps): boolean {
  if (!user) return false;
  return user?.account?.accountType === AccountType.Consultant ||
    user?.account?.accountType === AccountType.SolutionProvider;
}

export function isInquirerAccount(user?: UserProps): boolean {
  if (!user) return false;
  return user?.account?.accountType === AccountType.Inquirer;
}

/** PRINCE2 governance is a superadmin opt-in on customer (inquirer) accounts. */
export function isPrince2Account(user?: UserProps | null): boolean {
  return isInquirerAccount(user ?? undefined) && user?.account?.prince2Enabled === true;
}

export function isConsultantOrSolutionProvider(user?: UserProps): boolean {
  if (!user) return false;
  return user?.account?.accountType === AccountType.Consultant ||
    user?.account?.accountType === AccountType.SolutionProvider;
}

export function canSubmitOffer(user?: UserProps, offer?: { accountId: string; status: OfferStatus }): boolean {
  if (!user || !offer) return false;
  return user?.account?.id === offer.accountId && offer.status === OfferStatus.Draft;
}
